import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const products = [
    { name: t('nav.cmms'), path: '/cmms', description: t('nav.cmmsDesc') },
    { name: t('nav.chatbot'), path: '/chatbot', description: t('nav.chatbotDesc') },
  ];

  const services = [
    { name: t('nav.digitalTransformation'), path: '/digital-transformation', description: t('nav.digitalTransformationDesc') },
    { name: t('nav.customDevelopment'), path: '/custom-development', description: t('nav.customDevelopmentDesc') },
  ];

  const resources = [
    { name: t('nav.docs'), path: '/docs', description: t('nav.docsDesc') },
    { name: t('nav.help'), path: '/help', description: t('nav.helpDesc') },
  ];

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        (isScrolled || !isHome || isMobileMenuOpen) ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/">
            <span className="text-2xl font-bold gradient-text">mwbTechs</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Products Dropdown */}
            <div className="relative group">
              <button
                className="flex items-center gap-1 text-gray-700 hover:text-primary-600 transition-colors"
                onMouseEnter={() => setOpenDropdown('products')}
              >
                {t('nav.products')}
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {openDropdown === 'products' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-4"
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {products.map((product) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        className="block px-6 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-semibold text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.description}</div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Services Dropdown */}
            <div className="relative group">
              <button
                className="flex items-center gap-1 text-gray-700 hover:text-primary-600 transition-colors"
                onMouseEnter={() => setOpenDropdown('services')}
              >
                {t('nav.services')}
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {openDropdown === 'services' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-4"
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="block px-6 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-semibold text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-500">{service.description}</div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/pricing" className="text-gray-700 hover:text-primary-600 transition-colors">
              {t('nav.pricing')}
            </Link>

            {/* Resources Dropdown */}
            <div className="relative group">
              <button
                className="flex items-center gap-1 text-gray-700 hover:text-primary-600 transition-colors"
                onMouseEnter={() => setOpenDropdown('resources')}
              >
                {t('nav.resources')}
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {openDropdown === 'resources' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-4"
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {resources.map((resource) => (
                      <Link
                        key={resource.path}
                        to={resource.path}
                        className="block px-6 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-semibold text-gray-900">{resource.name}</div>
                        <div className="text-sm text-gray-500">{resource.description}</div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/contact" className="text-gray-700 hover:text-primary-600 transition-colors">
              {t('nav.contact')}
            </Link>
          </div>

          {/* CTA Buttons & Language Switcher */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login" className="text-gray-700 hover:text-primary-600 transition-colors font-medium">
              {t('nav.login')}
            </Link>
            <Link to="/signup" className="btn btn-primary">
              {t('nav.getStarted')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden py-4 border-t border-gray-200 bg-white"
            >
              <div className="space-y-4">
                {/* Mobile Products */}
                <div>
                  <button
                    onClick={() => toggleDropdown('products')}
                    className="flex items-center justify-between w-full py-2 text-gray-700"
                  >
                    {t('nav.products')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'products' && (
                    <div className="pl-4 mt-2 space-y-2">
                      {products.map((product) => (
                        <Link
                          key={product.path}
                          to={product.path}
                          className="block py-2 text-gray-600 hover:text-primary-600"
                        >
                          {product.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Services */}
                <div>
                  <button
                    onClick={() => toggleDropdown('services')}
                    className="flex items-center justify-between w-full py-2 text-gray-700"
                  >
                    {t('nav.services')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'services' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'services' && (
                    <div className="pl-4 mt-2 space-y-2">
                      {services.map((service) => (
                        <Link
                          key={service.path}
                          to={service.path}
                          className="block py-2 text-gray-600 hover:text-primary-600"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/pricing" className="block py-2 text-gray-700">{t('nav.pricing')}</Link>

                {/* Mobile Resources */}
                <div>
                  <button
                    onClick={() => toggleDropdown('resources')}
                    className="flex items-center justify-between w-full py-2 text-gray-700"
                  >
                    {t('nav.resources')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'resources' ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === 'resources' && (
                    <div className="pl-4 mt-2 space-y-2">
                      {resources.map((resource) => (
                        <Link
                          key={resource.path}
                          to={resource.path}
                          className="block py-2 text-gray-600 hover:text-primary-600"
                        >
                          {resource.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/contact" className="block py-2 text-gray-700">{t('nav.contact')}</Link>

                {/* Language Switcher in Mobile */}
                <div className="pt-4 pb-2">
                  <LanguageSwitcher className="w-full" />
                </div>

                <div className="pt-2 space-y-2">
                  <Link to="/login" className="block py-2 text-center border border-primary-600 text-primary-600 rounded-lg">
                    {t('nav.login')}
                  </Link>
                  <Link to="/signup" className="block py-2 text-center bg-primary-600 text-white rounded-lg">
                    {t('nav.getStarted')}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;
